import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { queryKeys, useAuthenticatedClient } from '@/api/queries'
import { POLL_NORMAL } from '@/api/polling'
import { invoiceKeys } from '@/features/invoices/keys'
import type { components } from '@/api/schema'

type CommentRead = components['schemas']['ProjectCommentRead']
type NewComment = components['schemas']['ProjectCommentCreate']

/**
 * Notes under an invoice and on its action items (USA-1180). They are the project's comments tagged with
 * the invoice, so saving one also refreshes the project's and its POs' comments.
 */
export function useInvoiceComments(invoiceUuid: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: invoiceKeys.comments(invoiceUuid),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/ap-invoices/{uuid}/comments', {
        params: { path: { uuid: invoiceUuid } },
      })
      if (error) throw error
      return data
    },
    enabled: !!invoiceUuid,
    refetchInterval: POLL_NORMAL,
  })
}

function useRefreshNotes(invoiceUuid: string) {
  const queryClient = useQueryClient()
  return () => {
    queryClient.invalidateQueries({ queryKey: invoiceKeys.comments(invoiceUuid) })
    // The AP list shows each invoice's newest note.
    queryClient.invalidateQueries({ queryKey: invoiceKeys.lists })
    queryClient.invalidateQueries({ queryKey: queryKeys.comments.all })
  }
}

export function useCreateInvoiceComment(invoiceUuid: string) {
  const getClient = useAuthenticatedClient()
  const refresh = useRefreshNotes(invoiceUuid)
  return useMutation({
    mutationFn: async (body: NewComment) => {
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/ap-invoices/{uuid}/comments', {
        params: { path: { uuid: invoiceUuid } },
        body,
      })
      if (error) throw error
      return data
    },
    onSuccess: refresh,
  })
}

export function useUpdateInvoiceComment(invoiceUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const refresh = useRefreshNotes(invoiceUuid)
  const listKey = invoiceKeys.comments(invoiceUuid)
  return useMutation({
    mutationFn: async ({ commentUuid, content }: { commentUuid: string; content: string }) => {
      const client = await getClient()
      const { data, error } = await client.PATCH('/dashboard/ap-invoices/{uuid}/comments/{comment_uuid}', {
        params: { path: { uuid: invoiceUuid, comment_uuid: commentUuid } },
        body: { content },
      })
      if (error) throw error
      return data
    },
    onMutate: async ({ commentUuid, content }) => {
      await queryClient.cancelQueries({ queryKey: listKey })
      const previous = queryClient.getQueryData<CommentRead[]>(listKey)
      queryClient.setQueryData<CommentRead[]>(listKey, (old) =>
        old?.map((c) =>
          c.uuid === commentUuid ? { ...c, content, updated_at: new Date().toISOString() } : c,
        ),
      )
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(listKey, context.previous)
    },
    onSettled: refresh,
  })
}

export function useDeleteInvoiceComment(invoiceUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const refresh = useRefreshNotes(invoiceUuid)
  const listKey = invoiceKeys.comments(invoiceUuid)
  return useMutation({
    mutationFn: async (commentUuid: string) => {
      const client = await getClient()
      const { error } = await client.DELETE('/dashboard/ap-invoices/{uuid}/comments/{comment_uuid}', {
        params: { path: { uuid: invoiceUuid, comment_uuid: commentUuid } },
      })
      if (error) throw error
    },
    onMutate: async (commentUuid) => {
      await queryClient.cancelQueries({ queryKey: listKey })
      const previous = queryClient.getQueryData<CommentRead[]>(listKey)
      queryClient.setQueryData<CommentRead[]>(listKey, (old) => old?.filter((c) => c.uuid !== commentUuid))
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(listKey, context.previous)
    },
    onSettled: refresh,
  })
}
